/**
 * Treasury Hooks - TanStack Query hooks for treasury module
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { treasuryService } from '../services/treasury.service';
import type {
  GetForecastsParams,
  CreateForecastParams,
  UpdateForecastParams,
  CreateRecurringExpenseParams,
  UpdateRecurringExpenseParams,
  CreateBudgetParams,
  UpdateBudgetParams,
  ForecastScenario,
} from '../types/treasury.types';

// Query keys
export const treasuryKeys = {
  all: ['treasury'] as const,
  dashboard: (companyId: string) => [...treasuryKeys.all, 'dashboard', companyId] as const,
  metrics: (companyId: string, period?: string) => [...treasuryKeys.all, 'metrics', companyId, period] as const,
  forecasts: (companyId: string) => [...treasuryKeys.all, 'forecasts', companyId] as const,
  forecastsList: (params: GetForecastsParams) => [...treasuryKeys.forecasts(params.companyId), params] as const,
  forecastDetail: (id: string, companyId: string) => [...treasuryKeys.forecasts(companyId), id] as const,
  forecastSummary: (companyId: string, startDate?: string, endDate?: string) =>
    [...treasuryKeys.forecasts(companyId), 'summary', startDate, endDate] as const,
  alerts: (companyId: string, status?: string) => [...treasuryKeys.all, 'alerts', companyId, status] as const,
  recommendations: (companyId: string, status?: string) => [...treasuryKeys.all, 'recommendations', companyId, status] as const,
  recurringExpenses: (companyId: string) => [...treasuryKeys.all, 'recurring-expenses', companyId] as const,
  recurringExpenseDetail: (id: string, companyId: string) => [...treasuryKeys.recurringExpenses(companyId), id] as const,
  upcomingOccurrences: (companyId: string, days?: number) => [...treasuryKeys.recurringExpenses(companyId), 'upcoming', days] as const,
  budgets: (companyId: string, status?: string) => [...treasuryKeys.all, 'budgets', companyId, status] as const,
  budgetDetail: (id: string, companyId: string) => [...treasuryKeys.budgets(companyId), id] as const,
  budgetAnalysis: (id: string, companyId: string) => [...treasuryKeys.budgets(companyId), id, 'analysis'] as const,
  healthScore: (companyId: string) => [...treasuryKeys.all, 'health-score', companyId] as const,
  healthScoreHistory: (companyId: string, months?: number) => [...treasuryKeys.healthScore(companyId), 'history', months] as const,
};

// ==================== DASHBOARD ====================

export function useTreasuryDashboard(companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.dashboard(companyId),
    queryFn: () => treasuryService.getDashboard(companyId),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useTreasuryMetrics(companyId: string, period?: string) {
  return useQuery({
    queryKey: treasuryKeys.metrics(companyId, period),
    queryFn: () => treasuryService.getMetrics(companyId, period),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });
}

// ==================== FORECASTS ====================

export function useCashForecasts(params: GetForecastsParams) {
  return useQuery({
    queryKey: treasuryKeys.forecastsList(params),
    queryFn: () => treasuryService.getForecasts(params),
    enabled: !!params.companyId,
  });
}

export function useCashForecast(id: string, companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.forecastDetail(id, companyId),
    queryFn: () => treasuryService.getForecastById(id, companyId),
    enabled: !!id && !!companyId,
  });
}

export function useCashForecastSummary(
  companyId: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: treasuryKeys.forecastSummary(companyId, startDate, endDate),
    queryFn: () => treasuryService.getForecastSummary(companyId, startDate, endDate),
    enabled: !!companyId,
  });
}

export function useCreateForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateForecastParams) => treasuryService.createForecast(params),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.forecasts(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.dashboard(variables.companyId) });
    },
  });
}

export function useUpdateForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      params,
    }: {
      id: string;
      companyId: string;
      params: UpdateForecastParams;
    }) => treasuryService.updateForecast(id, companyId, params),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.forecasts(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.forecastDetail(variables.id, variables.companyId) });
    },
  });
}

export function useDeleteForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      treasuryService.deleteForecast(id, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.forecasts(variables.companyId) });
    },
  });
}

export function useGenerateForecasts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      daysAhead,
      scenario,
    }: {
      companyId: string;
      daysAhead?: number;
      scenario?: ForecastScenario;
    }) => treasuryService.generateAutoForecasts(companyId, daysAhead, scenario),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.forecasts(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.dashboard(variables.companyId) });
    },
  });
}

// ==================== ALERTS ====================

export function useCashAlerts(companyId: string, status?: string) {
  return useQuery({
    queryKey: treasuryKeys.alerts(companyId, status),
    queryFn: () => treasuryService.getAlerts(companyId, status),
    enabled: !!companyId,
    refetchInterval: 60 * 1000, // Refresh every minute for real-time alerts
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      treasuryService.acknowledgeAlert(id, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.alerts(variables.companyId) });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      notes,
    }: {
      id: string;
      companyId: string;
      notes?: string;
    }) => treasuryService.resolveAlert(id, companyId, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.alerts(variables.companyId) });
    },
  });
}

export function useSnoozeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      until,
    }: {
      id: string;
      companyId: string;
      until: string;
    }) => treasuryService.snoozeAlert(id, companyId, until),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.alerts(variables.companyId) });
    },
  });
}

// ==================== RECOMMENDATIONS ====================

export function useCashRecommendations(companyId: string, status?: string) {
  return useQuery({
    queryKey: treasuryKeys.recommendations(companyId, status),
    queryFn: () => treasuryService.getRecommendations(companyId, status),
    enabled: !!companyId,
  });
}

export function useApplyRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      treasuryService.applyRecommendation(id, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recommendations(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.dashboard(variables.companyId) });
    },
  });
}

export function useDismissRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      reason,
    }: {
      id: string;
      companyId: string;
      reason?: string;
    }) => treasuryService.dismissRecommendation(id, companyId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recommendations(variables.companyId) });
    },
  });
}

// ==================== RECURRING EXPENSES ====================

export function useRecurringExpenses(companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.recurringExpenses(companyId),
    queryFn: () => treasuryService.getRecurringExpenses(companyId),
    enabled: !!companyId,
  });
}

export function useRecurringExpense(id: string, companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.recurringExpenseDetail(id, companyId),
    queryFn: () => treasuryService.getRecurringExpenseById(id, companyId),
    enabled: !!id && !!companyId,
  });
}

export function useUpcomingOccurrences(companyId: string, days?: number) {
  return useQuery({
    queryKey: treasuryKeys.upcomingOccurrences(companyId, days),
    queryFn: () => treasuryService.getUpcomingOccurrences(companyId, days),
    enabled: !!companyId,
  });
}

export function useCreateRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateRecurringExpenseParams) =>
      treasuryService.createRecurringExpense(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recurringExpenses(variables.companyId) });
    },
  });
}

export function useUpdateRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      params,
    }: {
      id: string;
      companyId: string;
      params: UpdateRecurringExpenseParams;
    }) => treasuryService.updateRecurringExpense(id, companyId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recurringExpenses(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recurringExpenseDetail(variables.id, variables.companyId) });
    },
  });
}

export function useDeleteRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      treasuryService.deleteRecurringExpense(id, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recurringExpenses(variables.companyId) });
    },
  });
}

export function useMarkOccurrencePaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      companyId,
      actualAmount,
    }: {
      occurrenceId: string;
      companyId: string;
      actualAmount?: number;
    }) => treasuryService.markOccurrencePaid(occurrenceId, companyId, actualAmount),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recurringExpenses(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.upcomingOccurrences(variables.companyId) });
    },
  });
}

// ==================== BUDGETS ====================

export function useBudgets(companyId: string, status?: string) {
  return useQuery({
    queryKey: treasuryKeys.budgets(companyId, status),
    queryFn: () => treasuryService.getBudgets(companyId, status),
    enabled: !!companyId,
  });
}

export function useBudget(id: string, companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.budgetDetail(id, companyId),
    queryFn: () => treasuryService.getBudgetById(id, companyId),
    enabled: !!id && !!companyId,
  });
}

export function useBudgetAnalysis(id: string, companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.budgetAnalysis(id, companyId),
    queryFn: () => treasuryService.getBudgetAnalysis(id, companyId),
    enabled: !!id && !!companyId,
  });
}

export function useCreateBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateBudgetParams) => treasuryService.createBudget(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.budgets(variables.companyId) });
    },
  });
}

export function useUpdateBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      companyId,
      params,
    }: {
      id: string;
      companyId: string;
      params: UpdateBudgetParams;
    }) => treasuryService.updateBudget(id, companyId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.budgets(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.budgetDetail(variables.id, variables.companyId) });
    },
  });
}

export function useDeleteBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      treasuryService.deleteBudget(id, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.budgets(variables.companyId) });
    },
  });
}

// ==================== FINANCIAL HEALTH ====================

export function useFinancialHealthScore(companyId: string) {
  return useQuery({
    queryKey: treasuryKeys.healthScore(companyId),
    queryFn: () => treasuryService.getHealthScore(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useHealthScoreHistory(companyId: string, months?: number) {
  return useQuery({
    queryKey: treasuryKeys.healthScoreHistory(companyId, months),
    queryFn: () => treasuryService.getHealthScoreHistory(companyId, months),
    enabled: !!companyId,
  });
}

// ==================== ML / AI INTELLIGENCE ====================

export const mlKeys = {
  all: ['treasury-ml'] as const,
  status: (companyId: string) => [...mlKeys.all, 'status', companyId] as const,
  healthScore: (companyId: string) => [...mlKeys.all, 'health-score', companyId] as const,
  clientRisk: (companyId: string, clientId: string) => [...mlKeys.all, 'client-risk', companyId, clientId] as const,
  advancedScoring: (companyId: string, clientId: string) => [...mlKeys.all, 'advanced-scoring', companyId, clientId] as const,
};

export function useMLStatus(companyId: string) {
  return useQuery({
    queryKey: mlKeys.status(companyId),
    queryFn: () => treasuryService.getMLStatus(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMLHealthScore(companyId: string) {
  return useQuery({
    queryKey: mlKeys.healthScore(companyId),
    queryFn: () => treasuryService.getMLHealthScore(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useClientPaymentRisk(companyId: string, clientId: string) {
  return useQuery({
    queryKey: mlKeys.clientRisk(companyId, clientId),
    queryFn: () => treasuryService.getClientPaymentRisk(companyId, clientId),
    enabled: !!companyId && !!clientId,
  });
}

export function useGenerateMLForecasts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      daysAhead,
    }: {
      companyId: string;
      daysAhead?: number;
    }) => treasuryService.generateMLForecasts(companyId, daysAhead),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.forecasts(variables.companyId) });
      queryClient.invalidateQueries({ queryKey: treasuryKeys.dashboard(variables.companyId) });
    },
  });
}

export function useGenerateMLRecommendations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => treasuryService.generateMLRecommendations(companyId),
    onSuccess: (data, companyId) => {
      queryClient.invalidateQueries({ queryKey: treasuryKeys.recommendations(companyId) });
    },
  });
}

// ==================== ADVANCED SCORING (Phase 6.2) ====================

import type {
  ClientPaymentHistoryData,
  ClientInvoiceData,
  AdvancedScoringOptions,
} from '../services/treasury.service';

/**
 * Get advanced multi-factor client scoring (BTP-optimized)
 */
export function useAdvancedClientScoring(
  companyId: string,
  clientId: string,
  paymentHistory: ClientPaymentHistoryData[],
  invoiceData: ClientInvoiceData[],
  options?: AdvancedScoringOptions
) {
  return useQuery({
    queryKey: mlKeys.advancedScoring(companyId, clientId),
    queryFn: () => treasuryService.getAdvancedClientScoring(
      companyId,
      clientId,
      paymentHistory,
      invoiceData,
      options
    ),
    enabled: !!companyId && !!clientId && paymentHistory.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes - scoring doesn't change frequently
  });
}

/**
 * Mutation for batch advanced scoring of multiple clients
 */
export function useBatchAdvancedScoring() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      clients,
      options,
    }: {
      companyId: string;
      clients: Array<{
        clientId: string;
        paymentHistory: ClientPaymentHistoryData[];
        invoiceData: ClientInvoiceData[];
      }>;
      options?: AdvancedScoringOptions;
    }) => treasuryService.getBatchAdvancedScoring(companyId, clients, options),
    onSuccess: (data, variables) => {
      // Invalidate individual scoring queries
      data.forEach((result) => {
        queryClient.invalidateQueries({
          queryKey: mlKeys.advancedScoring(variables.companyId, result.clientId),
        });
      });
    },
  });
}

// ==================== CONFIG ====================

export const configKeys = {
  all: ['treasury-config'] as const,
  config: (companyId: string) => [...configKeys.all, companyId] as const,
};

export function useTreasuryConfig(companyId: string) {
  return useQuery({
    queryKey: configKeys.config(companyId),
    queryFn: () => treasuryService.getConfig(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateTreasuryConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      companyId,
      config,
    }: {
      companyId: string;
      config: Partial<{
        alertThresholdCritical: number;
        alertThresholdWarning: number;
        forecastHorizonDays: number;
        recalculationFrequency: string;
        emailAlertsEnabled: boolean;
        aiRecommendationsEnabled: boolean;
      }>;
    }) => treasuryService.updateConfig(companyId, config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: configKeys.config(variables.companyId) });
      // Also invalidate ML status as it depends on config
      queryClient.invalidateQueries({ queryKey: mlKeys.status(variables.companyId) });
    },
  });
}
